// app/frontend/src/constants/theme.ts
// Centralized design tokens for WhereCanWeDance

// ─── Colors ───────────────────────────────────────────────────────────
export const COLORS = {
  // Primary accent
  orange: '#EA580C',
  orangeHover: '#c44b0a',
  // orange.5 — for orange TEXT sitting on an orange-tinted surface, where the
  // tint lifts background luminance and #EA580C drops under 4.5:1. Keep
  // COLORS.orange for fills, borders and text on plain dark backgrounds.
  orangeOnTint: '#f97316',

  // Backgrounds
  pageBg: '#1a1a1a',
  pageBgAlt: '#171717',
  pageBgDeep: '#0f0f0f',
  sectionBg: '#27272A',
  inputBg: '#222',
  cardBg: '#27272A',

  // Chrome layer backgrounds
  navBg: '#202020',                        // Navbar at top of page (solid, slightly elevated)
  navBgScrolled: 'rgba(18, 18, 18, 0.88)', // Navbar on scroll (blur glass)
  footerBg: '#0f0f0f',                     // Footer (intentionally darker for grounding)

  // Text
  textPrimary: '#f2f2f2',
  textDimmed: '#BBBBBB',    // 7.8:1 on #27272A — passes WCAG AA with strong margin
  textSubtle: '#999999',    // 5.8:1 on #1f1f1f card bg — passes 5:1 minimum
  textDescription: '#d1d1d1', // 9.8:1 on #27272A — passes WCAG AAA
  textCream: '#FFF7ED',     // 16.5:1 on #1a1a1a — warm cream for long-form reading (bio, tab icons)
  white: '#ffffff',         // Pure white; replaces the old Bootstrap/Tailwind text-white utility (#1952)
  textOnAccent: '#ffffff',  // Text on the orange accent (badges, pills, exported share frames)

  // Borders & Dividers
  border: '#444',
  borderLight: '#333',
  dividerShow: '#757575',
  dividerModal: '#333',

  // Feedback
  success: '#22c55e',
  error: '#ff6b6b',
  errorDark: '#dc2626',

  // Danger (destructive actions — archive, delete)
  danger: '#ef4444',
  dangerHover: '#dc2626',
  dangerAlpha05: 'rgba(239, 68, 68, 0.05)',
  dangerAlpha20: 'rgba(239, 68, 68, 0.20)',

  // Overlay / Alpha
  overlayBg: 'rgba(0, 0, 0, 0.7)',
  whiteAlpha10: 'rgba(255, 255, 255, 0.1)',
  whiteAlpha12: 'rgba(255, 255, 255, 0.12)',
  whiteAlpha06: 'rgba(255, 255, 255, 0.06)',
  whiteAlpha08: 'rgba(255, 255, 255, 0.08)',
  whiteAlpha20: 'rgba(255, 255, 255, 0.2)',
  orangeAlpha10: 'rgba(234, 88, 12, 0.1)',
  orangeAlpha20: 'rgba(234, 88, 12, 0.2)',
} as const;

// ─── Glassmorphism ────────────────────────────────────────────────────
// Flat panel for settings and dashboard surfaces (#2222): one fill, one hairline,
// no gradient or hover lift. Tiles that are links lift their border on hover.
export const PANEL = {
  background: COLORS.sectionBg,
  border: `1px solid ${COLORS.whiteAlpha08}`,
  borderRadius: '12px',
  borderHover: COLORS.whiteAlpha20,
} as const;

export const GLASSMORPHISM = {
  card: {
    // 3-stop matches the de facto pattern used across grid-item cards (EventCard,
    // VideoCard, ContentPostCard, and post-PR#634 PrivateLessonPageCard + RecentSupporterCard).
    // The middle stop adds subtle mid-tone depth that a 2-stop gradient lacks.
    background:
      'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(50,50,50,0.4) 35%, rgba(30,30,30,0.6) 100%)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: `1px solid ${COLORS.whiteAlpha10}`,
    borderRadius: '12px',
  },
  edit: {
    background:
      'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(40,40,40,0.4) 35%, rgba(25,25,25,0.7) 100%)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: `1px solid ${COLORS.whiteAlpha10}`,
    borderRadius: '12px',
    color: COLORS.textPrimary,
  },
  filter: {
    background:
      'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(80,80,80,0.35) 50%, rgba(40,40,40,0.5) 100%)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: `1px solid rgba(255, 255, 255, 0.1)`,
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2)',
  },
  authCard: {
    // Opaque enough to stay legible over the moving DottedWorldMap backdrop,
    // which the previous #17171750 / #75757530 values were not.
    background: 'rgba(23,23,23,0.55)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    border: `1px solid ${COLORS.whiteAlpha12}`,
    borderRadius: '14px',
  },
} as const;

// ─── Z-Index ──────────────────────────────────────────────────────────
/**
 * Size of the label above every themed field (text, select, number, date…).
 * One value so a form's labels never disagree: 13px, 12px on mobile, set by
 * --wcwd-input-label-size in styles/ThemedInputs.css. Labels sit below card
 * titles (15px) and section headings (16px) in the text hierarchy.
 * Checkbox and switch labels are option text beside the control, not field
 * labels, and keep their own size.
 */
export const INPUT_LABEL_FONT_SIZE = 'var(--wcwd-input-label-size, 0.8125rem)';

export const Z_INDEX = {
  footer: 10,
  showViewTabs: 100,
  themedModal: 200,
  navbar: 1000,
  themedDrawer: 2000,
  dropdowns: 10000,
  notifications: 10000,
  datePicker: 10001,
} as const;

// ─── Grid Spans ───────────────────────────────────────────────────────
export const CARD_GRID_SPAN = { base: 12, sm: 6, lg: 4 } as const;
export const CARD_GRID_SPAN_WIDE = { base: 12, sm: 6, md: 4, lg: 3 } as const;

// ─── Booking Status ───────────────────────────────────────────────────
// Shared across private lesson booking components (0=pending, 1=approved, 2=declined, 3=completed)
export const BOOKING_STATUS_TONE: Record<number, PillTone> = {
  0: 'pending',
  1: 'info',
  2: 'danger',
  3: 'success',
};

// ─── Roommate Posture ─────────────────────────────────────────────────
// Explicit gradients rather than Mantine's `variant="light"`. The light variant renders a muted tint that
// barely reads on the dark card, and it derives its colour from the theme
// palette — so a raw hex and a palette name behave differently in it.
//
// Green and blue are the pair that matters: someone holding a spare bed and
// someone who needs one. They should be distinguishable at a glance, because
// spotting that match is the entire job of the board.
//
// Violet for co-booking, not orange: orange is the brand, and it already
// carries the tab, the primary button and the ticket icon on this page. A badge
// in the same orange reads as "action", not as a category.
//
// Every stop clears 4.5:1 with white text — 12px badge copy is not large text.
// The first pass borrowed the old booking-status gradients' lighter stops, where white
// hit 2.54:1 on green. Those values are still live on the booking cards and
// still failing; fixing them is a separate change to another surface.
export const ROOMMATE_POSTURE_GRADIENT: Record<string, string> = {
  has_space:  'linear-gradient(135deg, #047857 0%, #065f46 100%)',
  needs_spot: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
  co_book:    'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
} as const;

// ─── Status Pill Tones ────────────────────────────────────────────────
// Flat-tinted pill recipe (Linear / Cal.com / Vercel product-pill pattern).
// bg  = tone at ~14% alpha (sits inside a dark card without shouting)
// fg  = tone lightened for readable text on the tinted bg (≥4.5:1 target)
// dot = solid tone at full strength for the leading indicator dot
export const PILL_TONE = {
  neutral:   { bg: 'rgba(148, 163, 184, 0.14)', fg: '#cbd5e1', dot: '#94a3b8' },
  info:      { bg: 'rgba(59, 130, 246, 0.14)',  fg: '#93c5fd', dot: '#3b82f6' },
  pending:   { bg: 'rgba(251, 191, 36, 0.14)',  fg: '#fcd34d', dot: '#f59e0b' },
  attention: { bg: 'rgba(234, 88, 12, 0.14)',   fg: '#fdba74', dot: '#f97316' },
  success:   { bg: 'rgba(16, 185, 129, 0.14)',  fg: '#6ee7b7', dot: '#10b981' },
  danger:    { bg: 'rgba(239, 68, 68, 0.14)',   fg: '#fca5a5', dot: '#ef4444' },
} as const;

export type PillTone = keyof typeof PILL_TONE;

// A count chip sitting on a solid orange surface (the active tab's fill),
// where the tinted pill tones wash out. White on 28% black measures 6.2:1
// over #EA580C and higher over the gradient's darker #C2410C end (#2078).
export const COUNT_ON_ACCENT = { bg: 'rgba(0, 0, 0, 0.28)', fg: '#ffffff' } as const;

// ─── Typography ───────────────────────────────────────────────────────
export const FONT = {
  family: 'Poppins, sans-serif',
  headerFamily: 'Rubik, sans-serif',
} as const;

// Landing and marketing pages only (#2053). Show and admin pages keep the
// compact H1/H2 defaults; a sales page needs a headline 3-4x body size.
export const MARKETING_TYPE = {
  /** Hero headline: 36px on phones up to 64px on desktop. */
  display: { fontSize: 'clamp(2.25rem, 1.25rem + 4.2vw, 4rem)', lineHeight: 1.02, letterSpacing: '-0.03em' },
  /** Section headings: 24px up to 32px. */
  section: { fontSize: 'clamp(1.5rem, 1.1rem + 1.6vw, 2rem)', lineHeight: 1.15, letterSpacing: '-0.02em' },
  /** The paragraph under a hero headline: 16px up to 18px. */
  lede: { fontSize: 'clamp(1rem, 0.9rem + 0.4vw, 1.125rem)', lineHeight: 1.65 },
  /** Plan prices. */
  price: { fontSize: 'clamp(2.25rem, 1.8rem + 1.5vw, 2.75rem)', lineHeight: 1, letterSpacing: '-0.02em' },
} as const;
